import React, { useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router";
import { Callout, Page, PageNotFound, QueryResult } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { AllowedTo } from "Components";
import { PERMISSION } from "Constants";
import {
  useAppDispatch,
  useAppSelector,
  useDialog,
  useUploadOption,
} from "Hooks";
import { UploadDialog } from "Modules/upload";
import { useGetPackageQuery, useGetVersionsQuery } from "Services";
import { selectDeletedVersions } from "Store/projects.slice";
import { SelectedVersionType, UploadOption, Version } from "Types";
import { clearVersions, versionsToPredecessors } from "Utils";
import ImportLatest from "../../components/ImportLatest/ImportLatest";
import ImportVersion from "../../components/ImportVersion/ImportVersion";
import ReleasesList from "../../components/ReleasesList/ReleasesList";
import DeletedPackage from "./DeletedPackage";
import LatestVersion from "./LatestVersion/LatestVersion";
import useFileUpload from "./useFileUpload";
import { getVersionByType } from "./utils";
import styles from "./versions.module.scss";

// Precedence for choosing a version when user hasn't yet selected one
const VERSION_TYPE_PRECEDENCE: SelectedVersionType[] = [
  "approved",
  "uploaded",
  "released",
];

const Versions = (): React.JSX.Element | null => {
  const dispatch = useAppDispatch();
  const { uploadOption, handleUploadOptionChange } = useUploadOption("VERSION");
  const [predecessor, setPredecessor] = useState<Version | null>(null);
  const uploadVersion = useDialog();
  const importVersion = useDialog();
  const importLatest = useDialog();
  const { groupId, packageId } = useParams();
  const {
    data: packageInfo,
    isFetching: isFetchingPackage,
    isLoading: isLoadingPackage,
  } = useGetPackageQuery(
    packageId
      ? {
          packageId: packageId,
        }
      : skipToken,
  );
  const { data, isFetching, isLoading, isError, error, refetch } =
    useGetVersionsQuery(
      packageId
        ? {
            packageId: packageId,
          }
        : skipToken,
    );
  const { uploadFile } = useFileUpload(packageId ?? "");
  const versions = useMemo(() => data?.versions ?? [], [data]);
  const deletedVersions = useAppSelector(selectDeletedVersions);
  const [selectedVersionType, setSelectedVersionType] =
    useState<SelectedVersionType | null>(null);

  const defaultVersionType: SelectedVersionType =
    VERSION_TYPE_PRECEDENCE.find((type) => {
      const version = getVersionByType(versions, type);
      return (
        version !== undefined &&
        version.version_id === packageInfo?.analysis_overview?.version_id
      );
    }) ?? "uploaded";
  const effectiveVersionType = selectedVersionType ?? defaultVersionType;
  const selectedVersion = getVersionByType(versions, effectiveVersionType);
  const hasDifferingProductNames =
    new Set(
      versions.map((version) => version.software_info.product).filter(Boolean),
    ).size > 1;

  const handleUploadButtonClick = (option: UploadOption) => {
    if (versions.length > 0) {
      setPredecessor(versions[0]);
    }

    if (option === "file") {
      uploadVersion.open();
    } else if (option === "url") {
      importVersion.open();
    }
  };

  if (isFetchingPackage || isLoadingPackage) {
    return <Page.Body status={{ status: "LOADING" }} />;
  } else if (!packageInfo) {
    return (
      <PageNotFound
        title="We can't find that package"
        byline="Try selecting a package from the sidebar"
      />
    );
  }

  return (
    <>
      <Page.Body>
        <QueryResult
          status={getQueryStatus({
            isLoading,
            isFetching,
            isError,
            error,
            data,
          })}
          disableFetching
        >
          <div className={styles.stack}>
            <AllowedTo permissions={[PERMISSION.CREATE_PACKAGE]}>
              {hasDifferingProductNames && (
                <Callout type="warning" title="Mismatched product names">
                  Versions in this package have differing product names in their
                  software info. The catalog card title uses the product name of
                  the last approved version.
                </Callout>
              )}
            </AllowedTo>
            <LatestVersion
              version={selectedVersion}
              selectedVersionType={effectiveVersionType}
              onSelectedVersionChange={setSelectedVersionType}
            />
            <ReleasesList
              data={data}
              packageInfo={packageInfo}
              uploadOption={uploadOption}
              onDerive={(version) => {
                setPredecessor(version);
                uploadVersion.open();
              }}
              onRefetch={() => {
                dispatch(clearVersions());
                refetch();
              }}
              onUpload={handleUploadButtonClick}
              onImportLatest={
                packageInfo.external_catalog ? importLatest.open : null
              }
              onUploadOptionChange={handleUploadOptionChange}
            />
          </div>
        </QueryResult>
      </Page.Body>
      <UploadDialog
        fileType="VERSION"
        isOpen={uploadVersion.isOpen}
        predecessor={
          predecessor
            ? { ...predecessor.software_info, id: predecessor.version_id }
            : null
        }
        predecessorOptions={versionsToPredecessors(versions, deletedVersions)}
        versionValues={
          versions?.map((version) => version.software_info.version) ?? []
        }
        maxVersionsLimit={data?.max_versions_per_package ?? 0}
        onClose={() => {
          uploadVersion.close();
          setPredecessor(null);
        }}
        onUpload={async (file, softwareInfo) => {
          await uploadFile(file, softwareInfo);
          setPredecessor(null);
        }}
      />
      <DeletedPackage
        projectId={packageInfo.project_id}
        packageId={packageInfo.package_id}
      />
      {groupId && packageInfo.external_catalog && (
        <ImportLatest
          isOpen={importLatest.isOpen}
          packageId={packageInfo.package_id}
          packageName={packageInfo.name}
          groupId={groupId}
          externalPackage={packageInfo.external_catalog}
          importedVersions={versions.map(
            (version) => version.software_info.version,
          )}
          onClose={importLatest.close}
        />
      )}
      {importVersion.isOpen && (
        <ImportVersion
          packageInfo={packageInfo}
          versions={versions}
          maxVersionsLimit={data?.max_versions_per_package ?? 0}
          isOpen={importVersion.isOpen}
          onClose={importVersion.close}
        />
      )}
    </>
  );
};

export default Versions;
